import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { type Color, colors } from "../../shared/colors";

type Weight = 200 | 400 | 600 | 800 | 1000;

const colorfulPaletteOptions = (
  weights: Weight[],
  restrictedColors?: Color[]
) => {
  const colorList = restrictedColors ?? colors;
  return colorList.flatMap((c) => weights.map((w) => `var(--${c}-${w})`));
};

export function shuffleColors(): Color[] {
  const shuffled = [...colors].sort(() => Math.random() - 0.5);
  const count = Math.floor(Math.random() * 4) + 1;
  return shuffled.slice(0, count);
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return hash >>> 0;
}

const tickerTimings = [40, 50, 70, 90, 120];

// https://en.wikipedia.org/wiki/Linear_congruential_generator#Parameters_in_common_use
const multiplier = 1103515245;
const increment = 12345;
const modulus = 0x7fffffff;

function seededRandom(seed: number) {
  return () => {
    seed = (seed * multiplier + increment) & modulus;
    return seed / modulus;
  };
}

type Ball = {
  cx: number;
  cy: number;
  r: number;
  color: string;
  tickerColors: string[];
  hasTicker: boolean;
  isMuted: boolean;
  key: string;
};

type Square = {
  x: number;
  y: number;
  size: number;
  color: string;
  isMuted: boolean;
  key: string;
};

type Props = {
  seed?: string;
  targetCircleSize?: number;
  mutingIntensity?: number;
  weights?: Weight[];
  animate?: boolean;
  animateFade?: boolean;
  animateAll?: boolean;
  restrictedColors?: Color[];
  grayscale?: boolean;
};

const mutingOpacity = 0.4;

export function CircleMesh({
  seed,
  targetCircleSize = 100,
  mutingIntensity = 0.5,
  weights = mutingIntensity === 0 ? [400, 600] : [200, 400],
  animate = true,
  animateFade = true,
  animateAll = false,
  restrictedColors,
  grayscale = false,
}: Props) {
  const randomSeed = useRef(seed ?? Math.random().toString());
  const random = seededRandom(hashString(randomSeed.current));
  const containerRef = useRef<SVGSVGElement>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const desaturateId = useId();
  const [tickerFrame, setTickerFrame] = useState(animate ? 0 : -1);
  const tickerFrameCount = 5;

  useEffect(() => {
    if (!animate || tickerFrame >= tickerFrameCount) return;

    const delay = tickerTimings[tickerFrame] ?? 100;
    const timeout = setTimeout(() => {
      setTickerFrame((f) => f + 1);
    }, delay);

    return () => clearTimeout(timeout);
  }, [animate, tickerFrame]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!containerSize || containerSize.width === 0) {
    return (
      <svg
        ref={containerRef}
        width="100%"
        height="100%"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      />
    );
  }

  const perRowCount = Math.max(
    1,
    Math.round(containerSize.width / targetCircleSize)
  );
  const totalRows = Math.ceil(
    perRowCount * (containerSize.height / containerSize.width)
  );

  // Limit palette size based on ball count
  const totalCount = totalRows * perRowCount;

  const colorList = restrictedColors ?? colors;

  const buildPalette = (paletteWeights: Weight[]) => {
    const palette = colorfulPaletteOptions(paletteWeights, restrictedColors)
      .sort(() => random() - 0.5)
      .slice(0, totalCount);
    for (const w of paletteWeights) {
      if (!palette.some((c) => c.endsWith(`-${w})`))) {
        const color = colorList[Math.floor(random() * colorList.length)]!;
        palette[Math.floor(random() * palette.length)] = `var(--${color}-${w})`;
      }
    }
    return palette;
  };

  const vividPalette = buildPalette(weights);
  const mutedPalette =
    mutingIntensity > 0 ? buildPalette([200, 400]) : vividPalette;

  const getColor = (isMuted: boolean) => {
    const palette = isMuted ? mutedPalette : vividPalette;
    const index = Math.floor(random() * palette.length);
    return palette[index]!;
  };

  const getTickerColors = (isMuted: boolean) => {
    const result: string[] = [];
    for (let i = 0; i < tickerFrameCount; i++) {
      result.push(getColor(isMuted));
    }
    return result;
  };

  const getDisplayColor = (finalColor: string, tickerColors: string[]) => {
    if (!animate || tickerFrame < 0 || tickerFrame >= tickerFrameCount)
      return finalColor;
    return tickerColors[tickerFrame] ?? finalColor;
  };

  // Use viewBox with perRowCount as width for exact horizontal fit
  // Each ball has diameter 1 in viewBox coordinates
  const viewBoxWidth = perRowCount;
  const ballDiameter = 1;
  const radius = ballDiameter / 2;

  const balls: Ball[][] = []; // 2D array: balls[rowIndex][colIndex]
  const squares: Square[] = [];

  // Pre-calculate colors for each grid intersection (where squares live)
  // This ensures color consistency when multiple balls reference the same square
  const squareColors = new Map<string, string>();

  const squareMuted = new Map<string, boolean>();

  const getSquareColor = (gridRow: number, gridCol: number): string => {
    const key = `${gridRow}-${gridCol}`;
    if (!squareColors.has(key)) {
      // Determine if square should be muted based on surrounding balls
      const ballBottomLeft = balls[gridRow - 1]?.[gridCol - 1];
      const ballBottomRight = balls[gridRow - 1]?.[gridCol];
      const ballTopLeft = balls[gridRow]?.[gridCol - 1];
      const ballTopRight = balls[gridRow]?.[gridCol];

      // Square is muted if:
      // - Any ball below is muted, OR
      // - Both balls above are muted, OR
      // - Only one ball above exists and it's muted
      const isMuted =
        mutingIntensity > 0 &&
        !!(
          ballBottomLeft?.isMuted ||
          ballBottomRight?.isMuted ||
          (ballTopLeft?.isMuted && ballTopRight?.isMuted) ||
          (ballTopLeft?.isMuted && !ballTopRight) ||
          (ballTopRight?.isMuted && !ballTopLeft)
        );

      squareMuted.set(key, isMuted);
      squareColors.set(key, getColor(isMuted));
    }
    return squareColors.get(key)!;
  };

  // Build rows from bottom to top
  const viewBoxHeight = totalRows;

  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    const y = viewBoxHeight - (rowIndex + 0.5) * ballDiameter;

    const rowBalls: Ball[] = [];

    for (let colIndex = 0; colIndex < perRowCount; colIndex++) {
      const x = (colIndex + 0.5) * ballDiameter;

      // Determine if this ball should be muted
      let isMuted = false;
      if (mutingIntensity >= 1) {
        isMuted = true;
      } else if (mutingIntensity > 0) {
        const ballBelow = balls[rowIndex - 1]?.[colIndex];
        const ballDiagonalLeft = balls[rowIndex - 1]?.[colIndex - 1];
        const ballDiagonalRight = balls[rowIndex - 1]?.[colIndex + 1];

        // If any ball below is muted, this ball is muted
        if (
          ballBelow?.isMuted ||
          ballDiagonalLeft?.isMuted ||
          ballDiagonalRight?.isMuted
        ) {
          isMuted = true;
        } else {
          // Base probability for seeding muting
          if (random() < mutingIntensity ** 2) {
            isMuted = true;
          }
        }
      }

      const color = getColor(isMuted);
      // Always generate ticker colors and consume random() to keep sequence deterministic
      const tickerColors = getTickerColors(isMuted);
      const tickerRoll = random() < 0.5;
      const hasTicker = animateAll || tickerRoll;
      rowBalls.push({
        cx: x,
        cy: y,
        r: radius,
        color,
        tickerColors: hasTicker ? tickerColors : [],
        hasTicker,
        isMuted,
        key: `${rowIndex}-${colIndex}`,
      });
    }

    balls.push(rowBalls);
  }

  // Generate 4 squares for each ball
  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    for (let colIndex = 0; colIndex < perRowCount; colIndex++) {
      const ball = balls[rowIndex]![colIndex]!;

      // Add slight overlap to prevent hairline gaps
      const overlap = 0.01;

      // Top-left square
      const tlColor = getSquareColor(rowIndex + 1, colIndex);
      const tlKey = `${rowIndex + 1}-${colIndex}`;
      squares.push({
        x: ball.cx - radius - overlap,
        y: ball.cy - radius - overlap,
        size: radius + overlap * 2,
        color: tlColor,
        isMuted: squareMuted.get(tlKey) ?? false,
        key: `${rowIndex}-${colIndex}-tl`,
      });

      // Top-right square
      const trColor = getSquareColor(rowIndex + 1, colIndex + 1);
      const trKey = `${rowIndex + 1}-${colIndex + 1}`;
      squares.push({
        x: ball.cx - overlap,
        y: ball.cy - radius - overlap,
        size: radius + overlap * 2,
        color: trColor,
        isMuted: squareMuted.get(trKey) ?? false,
        key: `${rowIndex}-${colIndex}-tr`,
      });

      // Bottom-left square
      const blColor = getSquareColor(rowIndex, colIndex);
      const blKey = `${rowIndex}-${colIndex}`;
      squares.push({
        x: ball.cx - radius - overlap,
        y: ball.cy - overlap,
        size: radius + overlap * 2,
        color: blColor,
        isMuted: squareMuted.get(blKey) ?? false,
        key: `${rowIndex}-${colIndex}-bl`,
      });

      // Bottom-right square
      const brColor = getSquareColor(rowIndex, colIndex + 1);
      const brKey = `${rowIndex}-${colIndex + 1}`;
      squares.push({
        x: ball.cx - overlap,
        y: ball.cy - overlap,
        size: radius + overlap * 2,
        color: brColor,
        isMuted: squareMuted.get(brKey) ?? false,
        key: `${rowIndex}-${colIndex}-br`,
      });
    }
  }

  // Flatten balls array for rendering
  const flatBalls = balls.flat();

  return (
    <svg
      ref={containerRef}
      width="100%"
      height="100%"
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMax slice"
      style={{
        ...(animate && animateFade
          ? {
              animation: "circleMeshFadeIn 0.5s ease-out",
              transformOrigin: "center bottom",
            }
          : null),
        ...(grayscale ? { filter: "grayscale(1) opacity(0.7)" } : null),
      }}
    >
      <defs>
        <style>
          {`
            @keyframes circleMeshFadeIn {
              from {
                opacity: 0;
                transform: scale(1.04);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes circleMeshFadeInNoScale {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
          `}
        </style>
        <filter id={desaturateId} colorInterpolationFilters="sRGB">
          <feColorMatrix
            in="SourceGraphic"
            type="saturate"
            values="0"
            result="gray"
          />

          <feFlood
            floodColor="var(--surface-primary)"
            floodOpacity="1"
            result="tint"
          />

          <feComposite
            in="gray"
            in2="tint"
            operator="arithmetic"
            k1="0"
            k2={mutingOpacity}
            k3={1 - mutingOpacity}
            k4="0"
            result="mixed"
          />

          <feComposite in="mixed" in2="SourceAlpha" operator="in" />
        </filter>
      </defs>
      <g filter={`url(#${desaturateId})`}>
        {squares
          .filter((s) => s.isMuted)
          .map((square) => (
            <rect
              key={square.key}
              x={square.x}
              y={square.y}
              width={square.size}
              height={square.size}
              fill={square.color}
            />
          ))}
      </g>
      {squares
        .filter((s) => !s.isMuted)
        .map((square) => (
          <rect
            key={square.key}
            x={square.x}
            y={square.y}
            width={square.size}
            height={square.size}
            fill={square.color}
          />
        ))}
      <g filter={`url(#${desaturateId})`}>
        {flatBalls
          .filter((b) => b.isMuted)
          .map((ball) => (
            <circle
              key={ball.key}
              cx={ball.cx}
              cy={ball.cy}
              r={ball.r}
              fill={getDisplayColor(ball.color, ball.tickerColors)}
            />
          ))}
      </g>
      {flatBalls
        .filter((b) => !b.isMuted)
        .map((ball) => (
          <circle
            key={ball.key}
            cx={ball.cx}
            cy={ball.cy}
            r={ball.r}
            fill={getDisplayColor(ball.color, ball.tickerColors)}
          />
        ))}
    </svg>
  );
}
